import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { BentoCard, HeroDeckPreview, MetricCard } from "../components/Visual";
import type { IconName } from "../components/Icons";

interface Feature {
  icon: IconName;
  title: string;
  body: string;
  feature?: boolean;
}

const FEATURES: Feature[] = [
  {
    icon: "wand",
    title: "Describe it, get a deck",
    body:
      "Type \"aggressive Boros prowess with burn\" and get a legal, playable 60-card list in any Magic format. Generation can take a little time while the engine warms up.",
    feature: true,
  },
  {
    icon: "archive",
    title: "Understands your collection",
    body:
      "Cap rares and mythics, pick featured sets, or restrict the pool to Arena-available cards. Caps are enforced after generation.",
  },
  {
    icon: "chart",
    title: "Analyze any decklist",
    body:
      "Paste an Arena export. See curve, color pips, mana sources, and get AI commentary on strengths, weaknesses, and swaps.",
  },
  {
    icon: "book",
    title: "Rulings on demand",
    body:
      "Built-in rulings lookup. No LLM call, no latency - just the official text the moment you need it.",
  },
  {
    icon: "cloud",
    title: "Daily card updates",
    body:
      "Admins pull the latest card data in one click, or schedule it nightly. The card pool stays current automatically.",
  },
  {
    icon: "shield",
    title: "Cheap and fair",
    body:
      "Free plan builds three decks a day, plenty for casual brewers. Pro is $3/month for unlimited builds - cancel anytime.",
  },
];

export function Home() {
  const { firebaseUser } = useAuth();

  return (
    <>
      <section className="hero hero--flush-top hero--showcase">
        <div className="hero-copy">
          <span className="eyebrow">
            <span className="dot" /> Foil Bloom AI deck OS
          </span>
          <h1>
            Brew like a
            <br />
            <span className="grad">future planeswalker.</span>
          </h1>
          <p className="tagline">
            Foil Bloom reads the card universe, checks legality,
            reasons through your collection, and turns a one-line idea into a
            playable deck with analysis, mana math, and MTG Arena export text.
          </p>
          <div className="cta">
            {firebaseUser ? (
              <Link to="/build" className="topbar-btn primary">Open the builder</Link>
            ) : (
              <>
                <Link to="/register" className="topbar-btn primary">Start brewing free</Link>
                <Link to="/community" className="topbar-btn">Explore decks</Link>
              </>
            )}
          </div>
        </div>

        <HeroDeckPreview />

        <div className="hero-meta hero-meta--cards">
          <MetricCard label="Free builds" value="3/day" detail="No card required" icon="bolt" />
          <MetricCard label="Pro" value="$3/mo" detail="Unlimited builds" icon="crown" />
          <MetricCard label="Inference" value="Local" detail="Ollama powered" icon="shield" />
        </div>
      </section>

      <main className="page home-landing">
        <section className="bloomy-home-feature" aria-labelledby="bloomy-home-title">
          <div className="bloomy-home-orb" aria-hidden>
            <img
              src="/bloomy.png"
              alt=""
              width={190}
              height={190}
              decoding="async"
              draggable={false}
            />
          </div>
          <div className="bloomy-home-copy">
            <span className="eyebrow"><span className="dot" /> Meet Bloomy</span>
            <h2 id="bloomy-home-title">Your collection-aware brewing companion.</h2>
            <p>
              Bloomy chats with you about your cards, saved decks, public brews,
              card data, and local MTG knowledge. Ask for upgrade paths, missing
              archetypes, synergy ideas, or a second opinion before spending wildcards.
            </p>
            <div className="bloomy-home-actions">
              <Link to={firebaseUser ? "/bloomy" : "/pricing"} className="topbar-btn primary">
                {firebaseUser ? "Chat with Bloomy" : "See Bloomy plans"}
              </Link>
              <span>Saved chats restore when you return.</span>
            </div>
          </div>
          <div className="bloomy-home-preview" aria-hidden>
            <div className="bloomy-home-bubble bloomy-home-bubble--user">
              What can I build from my collection tonight?
            </div>
            <div className="bloomy-home-bubble">
              I found three strong directions: Simic ramp, Boros tokens, and a
              low-rare Dimir tempo shell.
            </div>
            <div className="bloomy-home-pills">
              <span>Collection context</span>
              <span>Saved decks</span>
              <span>Chat history</span>
            </div>
          </div>
        </section>

        <div className="bento-grid home-bento">
          {FEATURES.map((f) => (
            <BentoCard key={f.title} icon={f.icon} title={f.title} feature={f.feature}>
              <p>{f.body}</p>
            </BentoCard>
          ))}
        </div>

        <section className="signal-band">
          <div>
            <span className="eyebrow"><span className="dot" /> Live workflow</span>
            <h2>From idea to Arena import without leaving the table.</h2>
          </div>
          <div className="signal-steps">
            <span>Prompt</span>
            <span>Pool</span>
            <span>Validate</span>
            <span>Export</span>
          </div>
        </section>
      </main>
    </>
  );
}
